/**
 * The only place `fetch` is called.
 *
 * Every request goes through here so error shapes, query-string building and
 * the base URL are defined once. Filter serialisation lives here too, which is
 * what lets the export links reuse exactly the search query.
 */
import type {
  FileImportResult,
  ImportSchema,
  JobCapabilities,
  ApiErrorPayload,
  BrowserSearchRequest,
  Capture,
  CurlCaptureRequest,
  DatasetStats,
  ImportableFile,
  Job,
  JobCreated,
  Judgment,
  JudgmentDetail,
  JudgmentFacets,
  JudgmentFilters,
  Page,
  SortDirection,
  SortField,
} from '@/types/api'

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

/** An error carrying the backend's structured payload. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly detail?: string

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message)
    this.name = 'ApiError'
    this.status = status
    this.code = payload.code
    this.detail = payload.detail
  }
}

/** True when the failure is a network/offline problem rather than an HTTP error. */
export function isOfflineError(error: unknown): boolean {
  return error instanceof TypeError
}

async function toApiError(response: Response): Promise<ApiError> {
  let payload: ApiErrorPayload = {
    code: 'http_error',
    message: `${response.status} ${response.statusText}`,
  }
  try {
    const body = await response.json()
    if (body && typeof body === 'object') {
      if (typeof body.code === 'string' && typeof body.message === 'string') {
        payload = body as ApiErrorPayload
      } else if (Array.isArray(body.detail)) {
        // FastAPI's own request-validation shape.
        const first = body.detail[0]
        payload = {
          code: 'validation_error',
          message: first?.msg ?? 'The request was rejected as invalid.',
          detail: Array.isArray(first?.loc) ? first.loc.join('.') : undefined,
        }
      } else if (typeof body.detail === 'string') {
        payload = { code: 'http_error', message: body.detail }
      }
    }
  } catch {
    /* a non-JSON error body; the status line is all we have */
  }
  return new ApiError(response.status, payload)
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: { accept: 'application/json', ...init?.headers },
    ...init,
  })
  if (!response.ok) throw await toApiError(response)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

async function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
}

// --------------------------------------------------------------------------
// Query-string building
// --------------------------------------------------------------------------

/**
 * Serialise filter state onto a URLSearchParams.
 *
 * Empty values are omitted rather than sent blank, so a bare search produces a
 * clean URL and the backend's defaults apply.
 */
export function filtersToParams(filters: JudgmentFilters): URLSearchParams {
  const params = new URLSearchParams()

  if (filters.q.trim()) params.set('q', filters.q.trim())
  filters.status.forEach((value) => params.append('status', value))
  filters.filingLocation.forEach((value) => params.append('filingLocation', value))
  if (filters.partyRole) params.set('partyRole', filters.partyRole)
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
  if (filters.dateTo) params.set('dateTo', filters.dateTo)
  if (filters.amountMin) params.set('amountMin', filters.amountMin)
  if (filters.amountMax) params.set('amountMax', filters.amountMax)
  if (filters.hasDetails !== null) params.set('hasDetails', String(filters.hasDetails))
  if (filters.captureId !== null) params.set('captureId', String(filters.captureId))

  return params
}

export interface SearchQuery {
  filters: JudgmentFilters
  page: number
  pageSize: number
  sortBy: SortField
  sortDir: SortDirection
}

function searchParams(query: SearchQuery): URLSearchParams {
  const params = filtersToParams(query.filters)
  params.set('page', String(query.page))
  params.set('pageSize', String(query.pageSize))
  params.set('sortBy', query.sortBy)
  params.set('sortDir', query.sortDir)
  return params
}

/** Absolute URL for a download, so it can be used as a plain link href. */
export function exportUrl(
  format: 'csv' | 'json' | 'xlsx',
  query: Omit<SearchQuery, 'page' | 'pageSize'>,
): string {
  const params = filtersToParams(query.filters)
  params.set('sortBy', query.sortBy)
  params.set('sortDir', query.sortDir)
  return `${API_BASE}/exports/judgments.${format}?${params}`
}

// --------------------------------------------------------------------------
// Endpoints
// --------------------------------------------------------------------------

export const api = {
  /** Ingestion routes this build supports; drives which tabs are offered. */
  capabilities: () => request<JobCapabilities>('/jobs/capabilities'),

  /** Columns an uploaded file may contain. */
  importSchema: () => request<ImportSchema>('/jobs/import-schema'),

  /**
   * Upload a CSV, JSON or Excel file.
   *
   * `dryRun` validates and reports without storing anything. The server runs
   * the same parse either way, so the preview cannot disagree with the import.
   */
  importFile: (file: File, options: { label?: string; dryRun?: boolean } = {}) => {
    const body = new FormData()
    body.append('file', file)
    if (options.label) body.append('label', options.label)
    body.append('dry_run', String(options.dryRun ?? false))
    // No content-type header: the browser must set the multipart boundary.
    return request<FileImportResult>('/jobs/file-import', { method: 'POST', body })
  },

  searchJudgments(query: SearchQuery): Promise<Page<Judgment>> {
    return request(`/judgments?${searchParams(query)}`)
  },

  getJudgment(id: number): Promise<JudgmentDetail> {
    return request(`/judgments/${id}`)
  },

  getFacets(filters: JudgmentFilters): Promise<JudgmentFacets> {
    return request(`/judgments/facets?${filtersToParams(filters)}`)
  },

  getStats(): Promise<DatasetStats> {
    return request('/judgments/stats')
  },

  suggestNames(prefix: string): Promise<string[]> {
    return request(`/judgments/suggest?q=${encodeURIComponent(prefix)}`)
  },

  previewExport(
    query: Omit<SearchQuery, 'page' | 'pageSize'>,
    format: 'csv' | 'json',
    limit = 100,
  ): Promise<unknown> {
    const params = filtersToParams(query.filters)
    params.set('sortBy', query.sortBy)
    params.set('sortDir', query.sortDir)
    params.set('fmt', format)
    params.set('limit', String(limit))
    return format === 'csv'
      ? fetch(`${API_BASE}/exports/preview?${params}`).then((r) => r.text())
      : request(`/exports/preview?${params}`)
  },

  listJobs(limit = 50): Promise<Job[]> {
    return request(`/jobs?limit=${limit}`)
  },

  getJob(id: number): Promise<Job> {
    return request(`/jobs/${id}`)
  },

  listImportable(): Promise<ImportableFile[]> {
    return request('/jobs/importable')
  },

  createJsonImport(paths: string[]): Promise<JobCreated> {
    return post('/jobs/json-import', { paths })
  },

  createCurlCapture(body: CurlCaptureRequest): Promise<JobCreated> {
    return post('/jobs/curl-capture', body)
  },

  createBrowserSearch(body: BrowserSearchRequest): Promise<JobCreated> {
    return post('/jobs/browser-search', body)
  },

  /**
   * A flat list of the most recent captures, for summary banners.
   *
   * Separate from the paged endpoint on purpose: the dashboard's capped-search
   * warning wants the latest handful and no pager, and unwrapping a paged
   * envelope to get them would be ceremony for nothing.
   */
  listCaptures(limit = 50): Promise<Capture[]> {
    return request(`/captures/recent?limit=${limit}`)
  },

  /**
   * One page of captures. Paged in the database, because the list grows once
   * per search and once per *part* of a split search — a capped surname can
   * add a dozen rows by itself.
   */
  listCapturePage(page = 1, pageSize = 20): Promise<Page<Capture>> {
    return request(`/captures?page=${page}&pageSize=${pageSize}`)
  },
}
